import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, X, Loader2, Camera, Eye, Trash2, ChevronLeft, ChevronRight, Send, Mic, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Modal } from "@/components/ui/modal";
import { AuraRing } from "@/components/brand/AuraRing";
import { VerifiedBadge } from "@/components/brand/VerifiedBadge";
import { MentionInput } from "@/components/common/MentionInput";
import { AudioPlayer } from "@/components/app/AudioPlayer";
import { AudioComposer } from "@/components/app/AudioComposer";
import { useAuth } from "@/context/AuthContext";
import { api, mediaUrl, timeAgo } from "@/lib/api";
import { cn } from "@/lib/utils";

// ---------- horizontal story tray ----------
export function StoryTray({ tray, onOpen, onAdd }) {
  if (!tray) return null;
  const groups = tray.groups || [];
  const mine = tray.mine;
  return (
    <div data-testid="story-tray" className="flex gap-3 overflow-x-auto border-b border-am-border px-4 py-3 no-scrollbar">
      <button data-testid="add-story" onClick={onAdd} className="flex shrink-0 flex-col items-center gap-1">
        <span className="relative">
          {mine ? (
            <AuraRing src={mediaUrl(mine.user.picture) || undefined} size={62} ring={2} fallback={mine.user.name} story storySeen />
          ) : (
            <span className="grid h-[62px] w-[62px] place-items-center rounded-full border-2 border-dashed border-am-border-strong bg-am-surface2">
              <Plus className="h-6 w-6 text-am-text2" />
            </span>
          )}
          <span className="absolute -bottom-0.5 -right-0.5 grid h-5 w-5 place-items-center rounded-full border-2 border-am-bg aura-gradient"><Plus className="h-3 w-3 text-black" strokeWidth={3} /></span>
        </span>
        <span className="max-w-[64px] truncate text-[11px] text-am-text2">Your Story</span>
      </button>
      {mine && (
        <button onClick={() => onOpen(groups, -1, mine)} className="flex shrink-0 flex-col items-center gap-1">
          <AuraRing src={mediaUrl(mine.user.picture) || undefined} size={62} ring={2} fallback={mine.user.name} story storySeen />
          <span className="max-w-[64px] truncate text-[11px] text-white">You</span>
        </button>
      )}
      {groups.map((g, i) => (
        <button key={g.user.user_id} data-testid={`story-${g.user.username}`} onClick={() => onOpen(groups, i)} className="flex shrink-0 flex-col items-center gap-1">
          <AuraRing src={mediaUrl(g.user.picture) || undefined} size={62} ring={2.5} fallback={g.user.name} story storySeen={g.all_seen} />
          <span className="max-w-[64px] truncate text-[11px] text-am-text2">{g.user.name?.split(" ")[0]}</span>
        </button>
      ))}
    </div>
  );
}

// ---------- fullscreen viewer ----------
export function StoryViewer({ groups, startIndex, mine, onClose, onViewed }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  // build ordered list: if startIndex === -1 view "mine" only, else groups from startIndex
  const list = startIndex === -1 && mine ? [mine] : groups.slice(Math.max(0, startIndex));
  const [gi, setGi] = useState(0);
  const [si, setSi] = useState(0);
  const [viewers, setViewers] = useState(null);
  const timerRef = useRef(null);

  const group = list[gi];
  const story = group?.stories[si];
  const isOwner = group && user && group.user.user_id === user.user_id;

  const advance = useCallback(() => {
    setSi((s) => {
      if (group && s + 1 < group.stories.length) return s + 1;
      setGi((g) => {
        if (g + 1 < list.length) { return g + 1; }
        onClose(); return g;
      });
      return 0;
    });
  }, [group, list.length, onClose]);

  useEffect(() => {
    if (!story) return;
    api.viewStory(story.id).then(() => onViewed && onViewed()).catch(() => {});
    setViewers(null);
    clearTimeout(timerRef.current);
    if (story.media.type !== "video" && story.media.type !== "audio") timerRef.current = setTimeout(advance, 5000);
    return () => clearTimeout(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [story?.id]);

  if (!group || !story) return null;

  const prev = () => { clearTimeout(timerRef.current); if (si > 0) setSi(si - 1); else if (gi > 0) { setGi(gi - 1); setSi(0); } };
  const del = async () => { try { await api.deleteStory(story.id); toast("Story deleted"); onClose(); onViewed && onViewed(); } catch (e) { toast(e.message); } };
  const loadViewers = async () => { try { setViewers(await api.storyViewers(story.id)); } catch { setViewers({ count: 0, viewers: [] }); } };

  return (
    <div data-testid="story-viewer" className="fixed inset-0 z-[80] flex items-center justify-center bg-black/95" onClick={onClose}>
      <div className="relative flex h-full w-full max-w-[480px] flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="absolute inset-x-0 top-0 z-10 flex gap-1 p-2">
          {group.stories.map((_, k) => (
            <span key={k} className="h-0.5 flex-1 overflow-hidden rounded-full bg-white/30">
              <span className={cn("block h-full bg-white", k < si ? "w-full" : k === si ? "w-full" : "w-0")} />
            </span>
          ))}
        </div>
        <div className="absolute inset-x-0 top-4 z-10 flex items-center gap-2 p-3">
          <button onClick={() => navigate(`/u/${group.user.username}`)} className="flex items-center gap-2">
            <AuraRing src={mediaUrl(group.user.picture) || undefined} size={34} ring={1.5} fallback={group.user.name} />
            <span className="flex items-center gap-1 text-sm font-semibold text-white">{group.user.name}{group.user.is_verified && <VerifiedBadge size={13} />}</span>
            <span className="text-xs text-white/60">{timeAgo(story.created_at)}</span>
          </button>
          <div className="ml-auto flex items-center gap-1">
            {isOwner && <button onClick={del} data-testid="story-delete" className="grid h-9 w-9 place-items-center rounded-full text-white hover:bg-white/10"><Trash2 className="h-5 w-5" /></button>}
            <button onClick={onClose} data-testid="story-close" className="grid h-9 w-9 place-items-center rounded-full text-white hover:bg-white/10"><X className="h-6 w-6" /></button>
          </div>
        </div>

        {story.media.type === "post"
          ? <SharedPostSlide post={story.post} onOpen={() => { clearTimeout(timerRef.current); onClose(); if (story.post?.available) navigate(`/post/${story.post.id}`); }} />
          : story.media.type === "video"
          ? <video src={mediaUrl(story.media.url)} autoPlay playsInline onEnded={advance} className="h-full w-full bg-black object-contain" />
          : story.media.type === "audio"
          ? (
            <div className="flex h-full w-full flex-col items-center justify-center gap-6 bg-gradient-to-b from-am-surface via-am-bg to-black px-8">
              <AuraRing src={mediaUrl(group.user.picture) || undefined} size={120} ring={3} fallback={group.user.name} />
              <div className="flex h-16 items-end gap-1">
                {Array.from({ length: 22 }).map((_, k) => <span key={k} className="w-1.5 rounded-full" style={{ height: `${20 + Math.abs(Math.sin(k * 1.7)) * 80}%`, background: "linear-gradient(180deg,#ff2e93,#ffd23c,#38e1ff)", animation: `pulse 1.1s ease-in-out ${k * 60}ms infinite` }} />)}
              </div>
              <div className="flex items-center gap-2 text-sm text-white/70"><Mic className="h-4 w-4 aura-text" /> Voice story</div>
              <audio src={mediaUrl(story.media.url)} autoPlay onEnded={advance} />
            </div>
          )
          : <img src={mediaUrl(story.media.url)} alt="" className="h-full w-full bg-black object-contain" />}

        {story.caption && <div className="absolute inset-x-0 bottom-24 px-6 text-center"><p className="inline-block rounded-2xl bg-black/50 px-4 py-2 text-sm text-white">{story.caption}</p></div>}

        {/* tap zones */}
        <button aria-label="previous" onClick={prev} className="absolute left-0 top-16 bottom-20 w-1/3" />
        <button aria-label="next" onClick={advance} className="absolute right-0 top-16 bottom-20 w-1/3" />
        <button onClick={prev} className="absolute left-2 top-1/2 hidden -translate-y-1/2 rounded-full bg-black/40 p-2 text-white md:block"><ChevronLeft className="h-5 w-5" /></button>
        <button onClick={advance} className="absolute right-2 top-1/2 hidden -translate-y-1/2 rounded-full bg-black/40 p-2 text-white md:block"><ChevronRight className="h-5 w-5" /></button>

        {isOwner && (
          <div className="absolute inset-x-0 bottom-4 flex justify-center">
            <button onClick={loadViewers} data-testid="story-viewers" className="flex items-center gap-1.5 rounded-full bg-black/50 px-4 py-2 text-sm text-white">
              <Eye className="h-4 w-4" /> {viewers ? `${viewers.count} viewer${viewers.count === 1 ? "" : "s"}` : "Viewers"}
            </button>
          </div>
        )}
        {isOwner && viewers && (
          <div className="absolute inset-x-0 bottom-16 mx-auto max-h-52 w-[90%] overflow-y-auto rounded-2xl border border-am-border bg-am-surface2 p-2">
            {viewers.viewers.length === 0 ? <p className="py-3 text-center text-xs text-am-text2">No views yet.</p>
              : viewers.viewers.map((v) => (
                <div key={v.user_id} className="flex items-center gap-2 rounded-lg px-2 py-1.5">
                  <AuraRing src={mediaUrl(v.picture) || undefined} size={28} ring={1.5} fallback={v.name} />
                  <span className="text-sm text-white">{v.name}</span>{v.is_verified && <VerifiedBadge size={12} />}
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- shared post slide ----------
function SharedPostSlide({ post, onOpen }) {
  const m = post?.available ? (post.media || []).find((x) => x.type === "image" || x.type === "video") : null;
  return (
    <div data-testid="story-post-slide" className="flex h-full w-full items-center justify-center bg-gradient-to-b from-am-surface via-am-bg to-black px-6">
      <button onClick={onOpen} data-testid="story-post-card" className="pointer-events-auto relative z-20 w-full max-w-[340px] overflow-hidden rounded-3xl border border-am-border bg-am-surface text-left shadow-2xl transition-transform active:scale-[0.98]">
        {!post?.available ? (
          <div className="p-8 text-center text-sm text-am-text2">This post is no longer available.</div>
        ) : (
          <>
            <div className="flex items-center gap-2 p-3">
              <AuraRing src={mediaUrl(post.author.picture) || undefined} size={32} ring={1.5} fallback={post.author.name} />
              <span className="flex min-w-0 items-center gap-1 text-sm font-semibold text-white"><span className="truncate">{post.author.name}</span>{post.author.is_verified && <VerifiedBadge size={12} />}</span>
              <span className="truncate text-xs text-am-text2">@{post.author.username}</span>
            </div>
            {m && (m.type === "video"
              ? <video src={mediaUrl(m.url)} poster={m.poster ? mediaUrl(m.poster) : undefined} muted playsInline className="max-h-[48vh] w-full bg-black object-cover" />
              : <img src={mediaUrl(m.url)} alt="" className="max-h-[48vh] w-full bg-black object-cover" />)}
            {post.text && <p className="line-clamp-4 px-3 pt-3 text-sm text-am-text">{post.text}</p>}
            <div className="flex items-center gap-1.5 px-3 py-3 text-xs font-bold aura-text"><ExternalLink className="h-3.5 w-3.5 text-aura-cyan" /> View post</div>
          </>
        )}
      </button>
    </div>
  );
}

// ---------- composer ----------
export function StoryComposer({ open, onClose, onPosted, post }) {
  const [media, setMedia] = useState(null);
  const [caption, setCaption] = useState("");
  const [uploading, setUploading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [voiceMode, setVoiceMode] = useState(false);
  const fileRef = useRef(null);

  const reset = () => { setMedia(null); setCaption(""); setVoiceMode(false); };
  const close = () => { onClose(); setTimeout(reset, 200); };

  const pick = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    setUploading(true);
    try { const r = await api.uploadMedia(f); setMedia({ url: r.url, type: r.type }); }
    catch (err) { toast("Upload failed", { description: err.message }); }
    finally { setUploading(false); }
  };

  const postIt = async () => {
    if (post) {
      setPosting(true);
      try { await api.sharePostToStory(post.id, caption.trim()); toast("Added to your story"); onPosted && onPosted(); close(); }
      catch (e) { toast(e.message); } finally { setPosting(false); }
      return;
    }
    if (!media) { toast("Add a photo, video or voice clip"); return; }
    setPosting(true);
    try { await api.createStory(media, caption.trim()); toast("Story posted"); onPosted && onPosted(); close(); }
    catch (e) { toast(e.message); } finally { setPosting(false); }
  };

  const pm = post ? (post.media || []).find((x) => x.type === "image" || x.type === "video") : null;
  return (
    <Modal open={open} onClose={close} title="Add to your Story" testid="story-composer">
      <div className="p-4">
        {post ? (
          <div data-testid="story-share-preview" className="overflow-hidden rounded-2xl border border-am-border bg-am-surface2">
            <div className="flex items-center gap-2 p-3">
              <AuraRing src={mediaUrl(post.author.picture) || undefined} size={30} ring={1.5} fallback={post.author.name} />
              <span className="truncate text-sm font-semibold text-white">{post.author.name}</span>
              <span className="truncate text-xs text-am-text2">@{post.author.username}</span>
            </div>
            {pm && (pm.type === "video" ? <video src={mediaUrl(pm.url)} poster={pm.poster ? mediaUrl(pm.poster) : undefined} muted className="max-h-56 w-full bg-black object-cover" /> : <img src={mediaUrl(pm.url)} alt="" className="max-h-56 w-full bg-black object-cover" />)}
            {post.text && <p className="line-clamp-3 p-3 text-sm text-am-text">{post.text}</p>}
            <p className="border-t border-am-border px-3 py-2 text-xs text-am-text3">Viewers can tap this card to open the post. Disappears after 24 hours.</p>
          </div>
        ) : media ? (
          <div className="relative overflow-hidden rounded-2xl border border-am-border bg-black">
            {media.type === "video" ? <video src={mediaUrl(media.url)} className="max-h-[50vh] w-full object-contain" controls />
              : media.type === "audio" ? <div className="p-4"><AudioPlayer src={media.url} /></div>
              : <img src={mediaUrl(media.url)} alt="" className="max-h-[50vh] w-full object-contain" />}
            <button onClick={() => setMedia(null)} className="absolute right-2 top-2 grid h-8 w-8 place-items-center rounded-full bg-black/60 text-white"><X className="h-4 w-4" /></button>
          </div>
        ) : voiceMode ? (
          <div>
            <AudioComposer onReady={(m) => { setMedia({ url: m.url, type: "audio" }); setVoiceMode(false); }} />
            <button onClick={() => setVoiceMode(false)} className="mt-2 text-xs font-semibold text-am-text2 hover:text-white">← Back to photo / video</button>
          </div>
        ) : (
          <div className="space-y-2">
            <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="story-pick" className="flex h-44 w-full flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-am-border-strong bg-am-surface2 text-am-text2 hover:text-white">
              {uploading ? <Loader2 className="h-7 w-7 animate-spin" /> : <Camera className="h-7 w-7" />}
              <span className="text-sm font-semibold">{uploading ? "Uploading…" : "Upload photo or video"}</span>
              <span className="text-xs">Disappears after 24 hours</span>
            </button>
            <button onClick={() => setVoiceMode(true)} data-testid="story-voice" className="flex w-full items-center justify-center gap-2 rounded-2xl border border-am-border bg-am-surface2 py-3 text-sm font-semibold text-am-text2 hover:text-white"><Mic className="h-4 w-4 aura-text" /> Record a voice story</button>
          </div>
        )}
        <input ref={fileRef} type="file" hidden accept="image/*,video/*" onChange={pick} />
        {(media || post) && (
          <MentionInput value={caption} onChange={(e) => setCaption(e.target.value)} data-testid="story-caption" placeholder="Add a caption… use @ to mention" maxLength={200} className="mt-3 rounded-xl border border-am-border bg-am-surface2 px-4 py-2.5 text-sm text-white outline-none focus:border-am-border-strong" />
        )}
        <button onClick={postIt} disabled={posting || (!media && !post)} data-testid="story-post" className="mt-3 flex w-full items-center justify-center gap-2 rounded-full aura-gradient py-3 text-sm font-bold text-black disabled:opacity-40">
          {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Share to Story
        </button>
      </div>
    </Modal>
  );
}
